<template>
  <div ref="mapContainerElement" class="map-container" :class="{'fullscreen': map.fullscreen}">
    <l-map :zoom="3"
           :center="[47.41322, -1.219482]"
           :options="{
             preferCanvas: true,
             // maxBounds: worldBounds,
             worldCopyJump: true,
           }"
           @ready="handleMapReady"
           @move="handleMapMove"
           @update:bounds="handleMapUpdateBounds">


      <l-tile-layer-custom :url.sync="map.mode.value" />

      <!-- canvas vessels layer -->
      <l-grid-layer :tile-component="map.tileComponent"
                    :options="{updateWhenZooming: false}"/>

      <!-- fullscreen switcher -->
      <l-control v-if="fullscreenSwitch" position="topleft">
        <div class="fullscreen-switcher">
          <i class="fa fa-arrows-alt" @click="toggleFullscreen"></i>
        </div>
      </l-control>

      <!-- map mode -->
      <l-control v-if="mapModeSwitch" position="bottomright">
        <b-form-group class="mb-0">
          <b-form-select
            class="map-overlay"
            v-model="map.mode.value"
            :options="map.mode.choices" />
        </b-form-group>
      </l-control>

      <!-- controls -->
      <l-control position="topleft">
        <!-- a hack to prevent controls position changes when map is redrawn -->
        <slot name="control-top-left"></slot>
      </l-control>

      <l-control position="topright">
        <!-- a hack to prevent controls position changes when map is redrawn -->
        <slot name="control-top-right"></slot>
      </l-control>

      <l-control position="bottomleft">
        <!-- a hack to prevent controls position changes when map is redrawn -->
        <slot name="control-bottom-left"></slot>
      </l-control>

      <l-control position="bottomright">
        <!-- a hack to prevent controls position changes when map is redrawn -->
        <slot name="control-bottom-right"></slot>
      </l-control>

      <!-- bubbles -->
      <template v-if="bubblesEnabled">
        <l-circle-marker
          v-for="(bubble, index) in bubblesFormatted"
          :key="`bubble-${index}`"
          :lat-lng="bubble.coords"
          :radius="bubble.radius"
          :color="bubble.color"
          :fillColor="bubble.fillColor">

          <rows-map-popup
            title="Details"
            :item-id="index"
            :rows="getBubblePopupData(bubble)" />

        </l-circle-marker>

        <!-- bubbles legend -->
        <l-control class="legend map-overlay" position="bottomleft">
          <p class="legend__item">
            <span class="circle circle-blue"></span>
            <span>Origin</span>
          </p>
          <p class="legend__item">
            <span class="circle circle-red"></span>
            <span>Destination</span>
          </p>
          <p v-if="maxBubbleVolume" class="legend__item">
            <span class="circle-size circle-grey"></span>
            <span>Largest bubble = {{ formatVolume(maxBubbleVolume) }}</span>
          </p>
        </l-control>
      </template>

      <!-- alerts -->
      <div class="alerts">
        <b-alert
          v-for="(alert, index) in alerts"
          :key="`alert-${index}`"
          :variant="alert.variant"
          :show="true"
          class="map-overlay"
          dismissible
          fade>
          <slot slot="dismiss">
            <div @click="$emit('alert-dismissed', alert)">&times;</div>
          </slot>
          {{ alert.text }}
        </b-alert>
      </div>


      <!-- optional map components -->
      <slot name="map-extras" v-bind:context="{ map }">
        <!--
          put additional leaflet components here
          <template #map-extras> ... </template>
        -->
      </slot>

    </l-map>
  </div>
</template>

<script>
import getNested from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';

import L from 'leaflet';
import {
  LMap,
  LControl,
  // LMarker,
  LCircleMarker,
  LGridLayer,
} from 'vue2-leaflet';

import { leafletBoundsToApi } from '@/shared/coordinate-utils';
import { mapModes } from '@/shared/constants/maps';
import LTileLayerCustom from '@/base_components/map/LTileLayerCustom';
import { formatVolume } from '@/shared/number-utils';
import RowsMapPopup from '@/base_components/map/RowsMapPopup';
import { getFormattedVesselData } from '@/shared/api_formatters/vessels';
import VesselsTileComponent from '@/base_components/map/VesselsTileComponent';
import { mapActions, mapMutations } from 'vuex';


export default {
  name: 'canvas-base-map',

  components: {
    RowsMapPopup,
    LMap,
    LTileLayerCustom,
    LGridLayer,
    LControl,
    // LMarker,
    LCircleMarker,
  },

  props: {
    fullscreenSwitch: {
      type: Boolean,
      default: true,
    },

    mapModeSwitch: {
      type: Boolean,
      default: true,
    },

    mapCenter: {
      // this parameter is used to .setZoom() the map when it's needed
      // must be an array or null/undefined
      type: Array,
      default: () => [47.41322, -1.219482],
    },

    mapZoom: {
      type: Number,
      default: 2,
    },

    mapBounds: {
      // this parameter is used to .fitBounds() the map when it's needed
      type: Array,
      default: null,
    },

    selectedVesselImo: {
      type: Number,
      default: null,
    },

    highlightedVesselImo: {
      type: Number,
      default: null,
    },

    selectedVesselDetails: {
      type: Object,
      default: () => ({}),
    },

    vesselsFilterParameters: {
      type: Object,
      default: () => ({}),
    },

    vesselsRetrievingMethod: {
      type: Function,
      default: () => {
        console.warn('vessels retrieving method not specified!');
      }
    },

    originBubbles: {
      type: Array,
      default: () => [],
    },

    destinationBubbles: {
      type: Array,
      default: () => [],
    },

    bubbleRadiusMultiplier: {
      type: Number,
      default: 300,  // here it's possible to adjust bubble sizes
    },

    alerts: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      worldBounds: L.latLngBounds(
        L.latLng(-90, -180),
        L.latLng(90, 180),
      ),

      map: {
        resizeObserver: null,
        fullscreen: false,
        instance: null,
        mode: {
          value: '',
          choices: mapModes,
        },
        tileComponent: VesselsTileComponent,
      },
    };
  },

  mounted() {
    this.$refs.mapContainerElement.addEventListener('keydown', this.keydownHandler);
  },

  beforeDestroy() {
    this.$refs.mapContainerElement.removeEventListener('keydown', this.keydownHandler);

    if(this.map.resizeObserver) {
      this.map.resizeObserver.unobserve(this.$refs.mapContainerElement);
    }

    // free map events bound in store, like zoomstart/zoomend
    this.unbindEvents();
  },

  methods: {
    ...mapMutations('map', [
      'setMapInstance',
      'setMapComponent',
      'setVesselsRetrievingMethod',
      'setVesselsFilterParameters',
      'clearVessels',
      'setSelectedVesselIMO',
      'setHighlightedVesselIMO',
      'setSelectedVesselDetails',
    ]),

    ...mapActions('map', [
      'bindEvents',
      'unbindEvents',
      'cacheAllVessels',
      'removeAllMarkers',
      'openVesselPopup',
    ]),

    formatVolume: formatVolume,

    keydownHandler(evt) {
      if (evt.key === 'Escape' && this.map.fullscreen) {
        // exit from fullscreen mode
        this.toggleFullscreen();
      }
    },

    async handleMapReady(mapObject) {
      // track map container dimesions and redraw a map if required
      this.resizeObserver = new ResizeObserver(
        this.handleMapContainerResized
      ).observe(this.$refs.mapContainerElement);

      this.map.instance = mapObject;

      if(this.mapBounds) {
        this.setMapBounds(this.mapBounds);
      }

      await this.handleMapUpdateBounds(this.map.instance.getBounds());
      this.$emit('map-ready', this.map.instance);

      // init store values/events
      this.setMapInstance(this.map.instance);
      this.setMapComponent(this);
      await this.bindEvents();
      this.setVesselsRetrievingMethod(this.vesselsRetrievingMethod);
    },

    handleMapContainerResized() {
      if(getNested(this, 'map.instance._mapPane')) {
        // prevents redraw errors in dev mode (when a component is live-updated in browser)
        this.map.instance.invalidateSize();
      }
    },

    async handleMapMove() {
      const bounds = this.map.instance.getBounds()
      this.$emit('map-move', leafletBoundsToApi(bounds));
    },

    async handleMapUpdateBounds(data) {
      this.$emit('map-update', leafletBoundsToApi(data));
    },

    getVesselCoords(vessel) {
      // console.log('vessel', JSON.stringify(vessel, null, 2));
      const coordinates = getNested(vessel, 'geometry.coordinates', [0.0, 0.0]);
      return [
        coordinates[1],
        coordinates[0],
      ];
    },

    getBubblePopupData(bubble) {
      return getNested(bubble, 'popupData', []);
    },

    toggleFullscreen() {
      // todo: fitBounds to the current bounds when fullscreen is toggled (both directions)
      this.map.fullscreen = !this.map.fullscreen;

      this.$nextTick(() => {
        if (this.map.fullscreen) {
          document.body.style.overflow = 'hidden';
        } else {
          document.body.style.overflow = 'auto';
        }
        this.map.instance.invalidateSize();
      });
    },

    setMapBounds(bounds) {
      if(bounds && this.map.instance) {
        this.map.instance.fitBounds(bounds, {maxZoom: 13, padding: [30, 30]});
      }
    },

    setMapCenterAndZoom() {
      if(this.map.instance) {
        this.map.instance.setView(this.mapCenter, this.mapZoom);
      }
    },
  },

  computed: {
    zoomInProgress() {
      return getNested(this.$store, 'state.map.zoomInProgress', null);
    },

    vesselPopupData() {
      if(this.selectedVesselDetails.imo === this.selectedVesselImo) {
        return getFormattedVesselData(this.selectedVesselDetails);
      } else {
        return {};
      }
    },

    bubblesEnabled() {
      return !isEmpty(this.originBubbles) || !isEmpty(this.destinationBubbles);
    },

    bubblesFormatted() {
      const origins = this.originBubbles.map(item => ({
        ...item,
        color: '#3388ff',
        fillColor: '#3388ff',
      }));

      const destinations = this.destinationBubbles.map(item => ({
        ...item,
        color: '#ff6361',
        fillColor: '#ff6361',
      }));

      return origins.concat(destinations).map(item => ({
        ...item,
        radius: Math.sqrt(this.bubbleRadiusMultiplier * item.volume / this.maxBubbleVolume),
      }));
    },

    maxBubbleVolume() {
      let volumes = this.originBubbles.map(item => item.volume);
      volumes = volumes.concat(this.destinationBubbles.map(item => item.volume));

      return isEmpty(volumes) ? 1 : Math.max(...volumes);
    },
  },

  watch: {
    mapBounds(bounds) {
      if(bounds && this.map.instance) {
        this.map.instance.fitBounds(
          bounds,
          { maxZoom: 13, padding: [30, 30] }
        );
      }
    },

    mapZoom() {
      this.setMapCenterAndZoom();
    },

    mapCenter() {
      this.setMapCenterAndZoom();
    },

    selectedVesselImo(vesselIMO) {
      this.setSelectedVesselIMO(vesselIMO);
      this.openVesselPopup(vesselIMO);  // map store action
    },

    highlightedVesselImo(vesselIMO) {
      this.setHighlightedVesselIMO(vesselIMO);
    },

    selectedVesselDetails() {
      this.setSelectedVesselDetails(
        {...this.selectedVesselDetails, popupData: this.vesselPopupData},
      );
    },

    async vesselsFilterParameters(apiData) {
      await this.removeAllMarkers();
      this.clearVessels();

      // it's needed to clear existing data before filter parameters update
      // VesselsTileComponent triger on a "vesselsFilterParameters" and vessels data should be consistent at the moment
      // otherwise prevously filtered vessels will be displayed
      this.setVesselsFilterParameters(apiData);

      // VesselsTileComponent is able to retrieve vessels for its bounds
      // it speed ups visual map update, whole map caching performs "in the background"
      await this.cacheAllVessels();
    },
  },
}

</script>


<style scoped>
  .map-container >>> .vessel-highlight {
    z-index: 2000 !important;
  }

  .map-container >>> .vessel-icon {
    z-index: 3000 !important;
    background-position: center;
    background-repeat: no-repeat;
    background-size: contain;
  }

  .map-container >>> .vessel-icon-arrow {
    width: 24px;
    height: 24px;
  }

  .map-container >>>  .vessel-icon-circle {
    width: 10px;
    height: 10px;
  }

  .map-container >>>  .leaflet-tooltip {
    font-size: 14px;
    font-weight: 800;
  }

  .map-container >>> .alert-dismissible .close {
    padding: 0.75rem 0.8rem;
  }

  .map-container >>> .leaflet-popup-content {
    min-width: 200px;
    min-height: 300px;
  }
</style>


<style scoped lang="scss">
  .map-container {
     z-index: 10;
    height: 100%;
    position: relative;

    // fullscreen toggle button
    &.fullscreen {
      position: fixed;
      height: 100vh;
      width: 100vw;
      left: 0;
      top: 0;
      bottom: 0;
      right: 0;
      z-index: 1050;
    }

    .vessel-count {
      padding: 0.25rem 0.5rem;
      border-radius: 3px;
    }

    // bubbles legend
    .legend {
      margin: 10px;
      padding: 5px 10px;
      border-radius: 5px;

      &__item {
        margin: 0;
        display: flex;
        justify-content: flex-start;
        align-items: center;
      }
      &__item:not(:last-child) {
        margin-bottom: 5px;
      }

      .circle {
        height: 12px;
        width: 12px;
        margin-right: 10px;
        border-radius: 50%;
      }

      .circle-size {
        height: 15px;
        width: 15px;
        margin-right: 10px;
        border-radius: 50%;
      }

      .circle-red {
        border: 3px rgb(255, 99, 97) solid;
        background-color: rgba(255, 99, 97, 0.2);
      }
      .circle-blue {
        border: 3px rgb(51, 136, 255) solid;
        background-color: rgba(51, 136, 255, 0.2);
      }
      .circle-grey {
        border: 3px rgb(177, 177, 177) solid;
        background-color: rgba(182, 184, 187, 0.2);
      }
    }

    .alerts {
      position: absolute;
      bottom: 5px;
      left: 220px;
      z-index: 500;
      min-width: 200px;
      max-width: calc(100% - 380px);

      .alert {
        margin-bottom: 5px;
      }
    }
  }
</style>
